// The art for the Business app's hero moments (practice/components/HeroMoment.jsx,
// the full screen "it worked" after a post, an offer, an approval, a top up...).
// Founder 2026-10-02: every moment gets its OWN big picture, not a row icon.
//
// The same house style and mechanism as shared/onboardingSectionArt.js: 160 by
// 120, free standing (no disc behind them), flat, rounded, no outlines, each
// object lifted off the page by a `depth` layer, sparkles in yellow, pink, mint
// and lavender.
//
// ⛔ Colours are {{slot}}s filled from the pastel tones only (fillIcon in
// shared/announcementIcons.js): never turquoise (the logo) or the link blue
// (actions). Decorative: the screen carries the words.
//
// Kept as XML so the same source renders in the app (SvgXml, MomentArt in
// practice/components/onboarding/SectionArt.jsx) and to PNG for review
// (practice/scripts/preview-section-art.mjs).

import { dot, line, lift, path, rect, sparkle } from './howItWorksArt.js'

const card = (x, y, w, h, r) => (f) => rect(x, y, w, h, r, f)
const turn = (deg, cx, cy, body) => `<g transform="rotate(${deg} ${cx} ${cy})">${body}</g>`
const move = (dx, dy, body) => `<g transform="translate(${dx} ${dy})">${body}</g>`
const tick = (cx, cy, r, w = 3) => line(`M${cx - r * 0.45} ${cy}l${r * 0.33} ${r * 0.33} ${r * 0.6}-${r * 0.62}`, '{{ink}}', w)
const badge = (cx, cy, r) => lift((f) => dot(cx, cy, r, f), '{{lime}}', 3, 3) + tick(cx, cy, r)
// A £ drawn as a stroke, centred on (cx, cy), about 13 by 17.
const pound = (cx, cy, stroke, w = 2.6) => move(cx - 120.5, cy - 92.5, line('M127 87c-1-2-3-3-5-3-3 0-5 2-5 5v12m-3-6h9m-9 6h13', stroke, w))
// A star (or, with many points and a shallow dip, a rosette's edge): n tips at R, dips at r.
function star(cx, cy, R, r, n = 5) {
  const pts = []
  for (let i = 0; i < n * 2; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / n
    const d = i % 2 ? r : R
    pts.push(`${(cx + Math.cos(a) * d).toFixed(2)} ${(cy + Math.sin(a) * d).toFixed(2)}`)
  }
  return path(`M${pts.join('L')}Z`)
}
// A price tag standing point up, 34 by 44, its top left at (x, y).
const tagUp = (x, y) => path(`M${x} ${y + 15}L${x + 14} ${y + 2}Q${x + 17} ${y - 1} ${x + 20} ${y + 2}L${x + 34} ${y + 15}V${y + 39}a5 5 0 0 1-5 5H${x + 5}a5 5 0 0 1-5-5z`)

const MOMENTS = {
  // A shift posted: a calendar page with one day lifted out of its slot as a
  // coral tile, and a lime tick.
  shiftPosted:
    lift(card(28, 28, 86, 80, 10), '{{lavender}}', 4, 4)
    + path('M28 38a10 10 0 0 1 10-10h66a10 10 0 0 1 10 10v8H28z')('{{pink}}')
    + rect(46, 18, 7, 18, 3.5, '{{yellow}}') + rect(89, 18, 7, 18, 3.5, '{{yellow}}')
    + [0, 1, 2].map((r) => [0, 1, 2, 3].map((c) => rect(36 + c * 18, 54 + r * 16, 14, 10, 3, r === 0 && c === 3 ? '{{softPink}}' : '{{white}}')).join('')).join('')
    + turn(-14, 124, 30, lift(card(108, 16, 34, 26, 6), '{{coral}}', 3, 3) + rect(116, 26, 18, 5, 2.5, '{{white}}'))
    + badge(116, 98, 15)
    + sparkle(20, 22, 8, '{{yellow}}') + sparkle(148, 64, 5, '{{mint}}') + sparkle(16, 98, 5, '{{pink}}'),

  // An offer sent: a mint paper plane on a dashed lavender trail.
  offerSent:
    line('M12 110Q24 86 56 88', '{{lavender}}', 4).replace('stroke-linecap', 'stroke-dasharray="6 8" stroke-linecap')
    + move(4, 4, path('M30 66L138 22L80 84Z')('{{depth}}') + path('M80 84L138 22L96 108Z')('{{depth}}'))
    + path('M80 84L138 22L96 108Z')('{{green}}')
    + path('M30 66L138 22L80 84Z')('{{mint}}')
    + '<path d="M80 84L138 22L86 96Z" fill="{{white}}" fill-opacity="0.35"/>'
    + sparkle(126, 82, 8, '{{yellow}}') + sparkle(38, 24, 6, '{{pink}}') + sparkle(150, 52, 4, '{{lavender}}'),

  // Extra time approved: a timesheet on a clipboard, a clock, a lime tick.
  overtimeApproved:
    lift(card(34, 20, 72, 90, 9), '{{coral}}', 4, 4)
    + rect(41, 32, 58, 72, 5, '{{white}}')
    + lift(card(56, 13, 28, 14, 6), '{{lavender}}', 2, 2) + dot(70, 20, 2.6, '{{white}}')
    + [0, 1, 2, 3].map((i) => dot(51, 46 + i * 14, 3.4, '{{mint}}') + rect(59, 44 + i * 14, i % 2 ? 22 : 30, 4, 2, '{{lavender}}', 0.6)).join('')
    + lift((f) => dot(112, 50, 22, f), '{{yellow}}', 3, 3) + dot(112, 50, 16, '{{white}}')
    + dot(112, 37, 1.8, '{{coral}}') + dot(125, 50, 1.8, '{{coral}}') + dot(112, 63, 1.8, '{{coral}}') + dot(99, 50, 1.8, '{{coral}}')
    + line('M112 50V40M112 50l8 5', '{{ink}}', 3) + dot(112, 50, 2.4, '{{ink}}')
    + badge(108, 96, 14)
    + sparkle(20, 30, 8, '{{pink}}') + sparkle(146, 18, 5, '{{lavender}}') + sparkle(142, 106, 5, '{{mint}}'),

  // An early finish confirmed: a clock stopped short of the hour, the time it
  // did not run marked pink, a small tick.
  earlyFinish:
    rect(52, 14, 48, 8, 4, '{{depth}}').replace('<rect', '<rect transform="translate(2 2)"') + rect(52, 14, 48, 8, 4, '{{coral}}')
    + lift((f) => dot(76, 64, 42, f), '{{mint}}', 4, 4) + dot(76, 64, 34, '{{white}}')
    + path('M76 64L49.3 48.6A30.8 30.8 0 0 1 76 33.2Z')('{{softPink}}')
    + [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
      const a = (i * Math.PI) / 6
      return dot((76 + Math.sin(a) * 28).toFixed(2), (64 - Math.cos(a) * 28).toFixed(2), i % 3 ? 1.4 : 2.2, i % 3 ? '{{lavender}}' : '{{coral}}')
    }).join('')
    + line('M76 64L55.2 52M76 64l12.1-7', '{{ink}}', 3.4) + dot(76, 64, 3, '{{ink}}')
    + badge(116, 98, 13)
    + sparkle(26, 26, 8, '{{yellow}}') + sparkle(140, 30, 5, '{{pink}}') + sparkle(22, 100, 5, '{{lavender}}'),

  // A cap raised: a price tag lifting off the top of a rising bar chart.
  capRaised:
    lift(card(26, 80, 24, 28, 6), '{{lavender}}', 3, 3)
    + lift(card(56, 64, 24, 44, 6), '{{mint}}', 3, 3)
    + lift(card(86, 46, 24, 62, 6), '{{yellow}}', 3, 3)
    + turn(-28, 112, 26,
      lift(path('M100 10H124Q128 10 128 14V38Q128 42 124 42H100Q98 42 96.6 40.6L85.4 28.4Q84 27 85.4 25.6L96.6 11.4Q98 10 100 10Z'), '{{coral}}', 3, 3)
      + dot(93, 26, 3.2, '{{white}}') + pound(112, 26, '{{white}}'))
    + lift((f) => dot(138, 62, 11, f), '{{lime}}', 2, 2) + line('M138 67.5v-11M133 61l5-5 5 5', '{{ink}}', 2.6)
    + sparkle(22, 30, 8, '{{yellow}}') + sparkle(52, 18, 5, '{{pink}}') + sparkle(146, 98, 5, '{{lavender}}'),

  // Matched to local rates: two price tags level, side by side on a balance.
  ratesMatched:
    lift(path('M80 82l15 24H65z'), '{{yellow}}', 3, 3)
    + lift(card(18, 74, 124, 9, 4.5), '{{lavender}}', 3, 3)
    + lift(tagUp(30, 28), '{{mint}}', 3, 3) + dot(47, 41, 3.2, '{{white}}') + pound(47, 59, '{{white}}')
    + lift(tagUp(96, 28), '{{coral}}', 3, 3) + dot(113, 41, 3.2, '{{white}}') + pound(113, 59, '{{white}}')
    + lift((f) => dot(80, 52, 11, f), '{{lime}}', 2, 2) + rect(74, 47, 12, 3.4, 1.7, '{{ink}}') + rect(74, 53.6, 12, 3.4, 1.7, '{{ink}}')
    + sparkle(80, 16, 8, '{{yellow}}') + sparkle(16, 34, 5, '{{pink}}') + sparkle(148, 40, 5, '{{mint}}'),

  // A review saved: a yellow rosette with ribbon tails and stars thrown out.
  reviewSaved:
    move(3, 3, path('M64.25 62L53 102.5l20.25-10.35Z')('{{depth}}') + path('M95.75 62L107 102.5l-20.25-10.35Z')('{{depth}}'))
    + path('M64.25 62L53 102.5l20.25-10.35Z')('{{softPink}}') + path('M95.75 62L107 102.5l-20.25-10.35Z')('{{pink}}')
    + move(3, 3, star(80, 48, 33, 29.4, 14)('{{depth}}')) + star(80, 48, 33, 29.4, 14)('{{yellow}}')
    + dot(80, 48, 23, '{{white}}')
    + move(1.5, 1.5, star(80, 49, 14, 6.2)('{{softPink}}')) + star(80, 49, 14, 6.2)('{{pink}}')
    + star(36, 26, 8, 2.8, 4)('{{lavender}}') + star(126, 20, 7, 3)('{{coral}}')
    + star(130, 70, 7, 2.6, 4)('{{mint}}') + star(30, 80, 6, 2.6)('{{lime}}')
    + sparkle(142, 44, 5, '{{yellow}}') + sparkle(20, 52, 4, '{{pink}}') + dot(124, 100, 3, '{{lavender}}'),

  // Topped up: a card and coins dropping into a wallet.
  toppedUp:
    turn(-10, 76, 50, lift(card(46, 28, 62, 40, 7), '{{coral}}', 3, 3) + rect(55, 36, 13, 10, 2.5, '{{yellow}}') + rect(84, 38, 16, 5, 2.5, '{{white}}'))
    + lift((f) => dot(124, 24, 11, f), '{{yellow}}', 2, 2) + dot(124, 24, 5, '{{white}}')
    + lift((f) => dot(138, 46, 8, f), '{{yellow}}', 2, 2) + dot(138, 46, 3.5, '{{white}}')
    + line('M124 4v5M138 28v4', '{{lavender}}', 2.6)
    + lift(card(30, 58, 100, 52, 11), '{{lavender}}', 4, 4)
    + lift(card(104, 74, 34, 22, 9), '{{softPink}}', 2, 2) + dot(116, 85, 4, '{{white}}')
    + sparkle(22, 30, 8, '{{pink}}') + sparkle(150, 74, 5, '{{mint}}') + sparkle(16, 96, 5, '{{yellow}}'),

  // Help improve TempDash (the support survey's intro, both apps): a page of
  // notes, a coral pencil across it, a yellow star.
  feedback:
    turn(-6, 70, 61, lift(card(34, 14, 72, 94, 9), '{{lavender}}', 4, 4)
      + rect(42, 24, 56, 76, 5, '{{white}}')
      + [0, 1, 2, 3].map((i) => rect(50, 36 + i * 14, i === 3 ? 24 : 40, 5, 2.5, '{{lavender}}', 0.6)).join(''))
    + move(3, 3, star(118, 30, 19, 8.5)('{{depth}}')) + star(118, 30, 19, 8.5)('{{yellow}}')
    + turn(-35, 108, 86,
      move(3, 3, path('M76 86.5L88 79H140v15H88Z')('{{depth}}'))
      + rect(88, 79, 44, 15, 2, '{{coral}}') + rect(132, 79, 8, 15, 2, '{{pink}}')
      + path('M76 86.5L88 79V94Z')('{{softPink}}') + path('M76 86.5L81 83.4V89.6Z')('{{ink}}'))
    + sparkle(20, 26, 8, '{{pink}}') + sparkle(146, 62, 5, '{{mint}}') + sparkle(18, 96, 5, '{{yellow}}'),

  // Thanks for your feedback: a mint speech bubble holding a pink heart, a lime tick.
  feedbackThanks:
    lift(path('M30 18h84a12 12 0 0 1 12 12v44a12 12 0 0 1-12 12H66l-20 16 3-16H30a12 12 0 0 1-12-12V30a12 12 0 0 1 12-12z'), '{{mint}}', 4, 4)
    + move(1.5, 1.5, path('M72 72C58 62 50 54 50 45a11 11 0 0 1 22-4 11 11 0 0 1 22 4c0 9-8 17-22 27Z')('{{softPink}}'))
    + path('M72 72C58 62 50 54 50 45a11 11 0 0 1 22-4 11 11 0 0 1 22 4c0 9-8 17-22 27Z')('{{pink}}')
    + badge(126, 94, 14)
    + sparkle(146, 24, 8, '{{yellow}}') + sparkle(14, 26, 5, '{{lavender}}') + sparkle(20, 106, 5, '{{coral}}'),
}

const svg = (body) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 120">${body}</svg>`

export const HERO_MOMENT_XML = Object.fromEntries(Object.entries(MOMENTS).map(([k, b]) => [k, svg(b)]))
